import type { ButtonProps as MuiButtonProps } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { type ReactNode } from 'react'
import { withInfoTooltip } from 'app/core/components/InfoTooltip'
import { compose } from 'app/utils/fp'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Progress from 'core/components/progress/Progress'
import type Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import Text from '../Text'

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'background'> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'cta' | 'text'
  size?: 'small' | 'medium' | 'large'
  children: string | ReactNode
  disabled?: boolean
  loading?: boolean
  icon?: string
  tooltip?: string | ReactNode
  rightIcon?: string
  solidIcon?: boolean
}

const Button = ({
  variant = 'primary',
  size = 'medium',
  className = undefined,
  children,
  disabled = false,
  loading = false,
  icon = undefined,
  rightIcon = undefined,
  onClick,
  solidIcon = false,
  ...rest
}: ButtonProps) => {
  const hasRightIcon = !!rightIcon
  const hasLeftIcon = !!icon
  const isDropdown = rightIcon === 'angle-down'
  const classes = useStyles()
  const spanTextVariant = size === 'large' ? 'buttonPrimary' : 'buttonSecondary'
  return (
    <button
      data-testid={generateTestId(children)}
      className={clsx(
        classes.button,
        variant === 'text'
          ? classes.buttonVariantText
          : classes[`button${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
        classes[`button${size.charAt(0).toUpperCase() + size.slice(1)}`],
        {
          [classes.buttonHasLeftIcon]: hasLeftIcon,
          [classes.buttonHasRightIcon]: hasRightIcon && !hasLeftIcon,
          [classes.buttonIsDropdown]: isDropdown,
        },
        className,
        { disabled, loading },
      )}
      disabled={disabled || loading}
      onClick={disabled || loading ? undefined : onClick}
      {...rest}
    >
      <Text
        className={clsx(
          'button-text',
          classes.buttonText,
          classes[`buttonText${size.charAt(0).toUpperCase() + size.slice(1)}`],
          {
            [classes.iconTextHasLeft]: hasLeftIcon && !hasRightIcon,
            [classes.iconTextHasRight]: hasRightIcon && !hasLeftIcon,
            [classes.iconTextHasBoth]: hasLeftIcon && hasRightIcon,
            [classes.iconTextDropdown]: isDropdown,
          },
        )}
        component="div"
        variant={spanTextVariant}
      >
        {!!icon && !loading && (
          <FontAwesomeIcon size="md" solid={solidIcon} className="button-icon">
            {icon}
          </FontAwesomeIcon>
        )}
        {loading ? (
          <Progress
            inline
            overlay={false}
            message={null}
            loading
            inlineClassName={classes.inheritColors}
          />
        ) : (
          children
        )}
        {!!rightIcon && !loading && (
          <FontAwesomeIcon size="md" className="button-icon button-right-icon">
            {rightIcon}
          </FontAwesomeIcon>
        )}
      </Text>
    </button>
  )
}

interface StyleProps extends ButtonProps {
  hasRightIcon: boolean
  hasLeftIcon: boolean
  isDropdown: boolean
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  isDropdown: {
    minWidth: 150,
  },
  inheritColors: {
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    fontStyle: 'inherit',
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    fontStretch: 'inherit',
    letterSpacing: 'inherit',
  },
  iconText: {
    justifyContent: 'center',
    gridTemplateColumns: 'max-content',
    gridTemplateAreas: 'unset',
  },
  iconTextHasLeft: {
    justifyContent: 'start',
    gridTemplateColumns: 'max-content max-content',
  },
  iconTextHasRight: {
    gridTemplateColumns: 'max-content max-content',
  },
  iconTextHasBoth: {
    gridTemplateColumns: 'max-content max-content max-content',
  },
  iconTextDropdown: {
    gridTemplateAreas: '"button-icon button-text button-right-icon"',
  },
  buttonText: {
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '& .loading-ellipsis-animation': {
      position: 'relative',
    },
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  buttonTextMedium: {
    fontSize: 14,
  },
  buttonTextLarge: {
    fontSize: 16,
  },
  button: {
    cursor: 'pointer',
    outline: 0,
    transition:
      'background 0.2s ease, color 0.2s ease, border-color 0.2s ease, border-radius 0.3s ease',
    borderRadius: 4,

    '& .button-icon': {
      marginTop: 2,
      fontWeight: 400,
    },
    '& .button-icon, & .button-text': {
      transition: 'color 0.2s ease',
    },
    '& .button-right-icon': {
      fontSize: undefined,
    },

    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      '& .button-icon, & .button-text': {},
    },
    '&.disabled': {
      cursor: 'not-allowed',
      '& .button-icon, & .button-text': {},
    },
    '&.loading': {
      cursor: 'not-allowed',
    },
  },
  buttonSmall: {
    height: 32,
    padding: '4px 18px',
  },
  buttonMedium: {
    height: 36,
    padding: '4px 20px',
  },
  buttonLarge: {
    height: 40,
    padding: '4px 20px',
  },
  buttonHasLeftIcon: {
    '& $buttonSmall': {
      padding: '4px 18px',
    },
    '& $buttonMedium': {
      padding: '8px 18px',
    },
    '& $buttonLarge': {
      padding: '12px 18px',
    },
  },
  buttonHasRightIcon: {
    '& $buttonSmall': {
      padding: '4px 18px',
    },
    '& $buttonMedium': {
      padding: '8px 18px',
    },
    '& $buttonLarge': {
      padding: '12px 18px',
    },
  },
  buttonIsDropdown: {
    '& .button-icon': {
      gridArea: 'button-icon',
    },
    '& .button-right-icon': {
      gridArea: 'button-right-icon',
      fontSize: 18,
    },
  },
  buttonPrimary: {
    border: `1px solid ${theme.components.button.primary.border}`,
    backgroundColor: theme.components.button.primary.background,
    color: theme.components.button.primary.color,
    '& .button-icon, & .button-text': {
      color: theme.components.button.primary.color,
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: theme.components.button.primary.activeBackground,
      borderColor: theme.components.button.primary.activeBorder,
      color: theme.components.button.primary.activeColor,
      '& .button-icon, & .button-text': {
        color: theme.components.button.primary.activeColor,
      },
    },
    '&.disabled': {
      backgroundColor: `${theme.components.button.primary.disabledBackground} !important`,
      borderColor: `${theme.components.button.primary.disabledBorder} !important`,
      color: `${theme.components.button.primary.color} !important`,
      '& .button-icon, & .button-text': {
        color: theme.components.button.primary.color,
      },
    },
    '&.loading': {
      backgroundColor: `${theme.components.button.primary.background} !important`,
      borderColor: `${theme.components.button.primary.border} !important`,
      color: `${theme.components.button.primary.color} !important`,
    },
  },
  buttonSecondary: {
    border: `1px solid ${theme.components.button.secondary.border}`,
    backgroundColor: theme.components.button.secondary.background,
    color: theme.components.button.secondary.color,
    '& .button-icon, & .button-text': {
      color: theme.components.button.secondary.color,
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: theme.components.button.secondary.activeBackground,
      borderColor: theme.components.button.secondary.activeBorder,
      color: theme.components.button.secondary.activeColor,
      '& .button-icon, & .button-text': {
        color: theme.components.button.secondary.activeColor,
      },
    },
    '&.disabled': {
      backgroundColor: `${theme.components.button.secondary.disabledBackground} !important`,
      borderColor: `${theme.components.button.secondary.disabledBorder} !important`,
      color: `${theme.components.button.secondary.disabledColor} !important`,
      '& .button-icon, & .button-text': {
        color: `${theme.components.button.secondary.disabledColor} !important`,
      },
    },
    '&.loading': {
      backgroundColor: `${theme.components.button.secondary.background} !important`,
      borderColor: `${theme.components.button.secondary.border} !important`,
      color: `${theme.components.button.secondary.color} !important`,
    },
  },
  buttonTertiary: {
    border: `1px solid ${theme.components.button.tertiary.border}`,
    backgroundColor: theme.components.button.tertiary.background,
    color: theme.components.button.tertiary.color,
    '& .button-icon, & .button-text': {
      color: theme.components.button.tertiary.color,
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: theme.components.button.tertiary.activeBackground,
      borderColor: theme.components.button.tertiary.activeBorder,
      color: theme.components.button.tertiary.activeColor,
      '& .button-icon, & .button-text': {
        color: theme.components.button.tertiary.activeColor,
      },
    },
    '&.disabled': {
      backgroundColor: `${theme.components.button.tertiary.disabledBackground} !important`,
      borderColor: `${theme.components.button.tertiary.disabledBorder} !important`,
      color: `${theme.components.button.tertiary.color} !important`,
      '& .button-icon, & .button-text': {
        color: theme.components.button.tertiary.color,
      },
    },
    '&.loading': {
      backgroundColor: `${theme.components.button.tertiary.background} !important`,
      borderColor: `${theme.components.button.tertiary.border} !important`,
      color: `${theme.components.button.tertiary.color} !important`,
    },
  },
  buttonCta: {
    border: `1px solid ${theme.components.button.cta.border}`,
    backgroundColor: theme.components.button.cta.background,
    color: theme.components.button.cta.color,
    '& .button-icon, & .button-text': {
      color: theme.components.button.cta.color,
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: theme.components.button.cta.activeBackground,
      borderColor: theme.components.button.cta.activeBorder,
      color: theme.components.button.cta.activeColor,
      '& .button-icon, & .button-text': {
        color: theme.components.button.cta.activeColor,
      },
    },
    '&.disabled': {
      backgroundColor: `${theme.components.button.cta.disabledBackground} !important`,
      borderColor: `${theme.components.button.cta.disabledBorder} !important`,
      color: `${theme.components.button.cta.color} !important`,
      '& .button-icon, & .button-text': {
        color: theme.components.button.cta.color,
      },
    },
    '&.loading': {
      backgroundColor: `${theme.components.button.cta.background} !important`,
      borderColor: `${theme.components.button.cta.border} !important`,
      color: `${theme.components.button.cta.color} !important`,
    },
  },
  buttonVariantText: {
    border: 'none',
    backgroundColor: 'transparent',
    color: theme.components.button.text.color,
    padding: '2px 4px',
    height: 'auto',
    minHeight: 'unset',
    '& .button-icon, & .button-text': {
      color: theme.components.button.text.color,
    },
    '&:hover, &.hover, &:active, &.active, &:focus, &.focus': {
      backgroundColor: 'transparent',
      color: theme.components.button.text.activeColor,
      fontWeight: 900,
      '& .button-icon, & .button-text': {
        color: theme.components.button.text.activeColor,
      },
    },
    '&.disabled': {
      cursor: 'not-allowed',
      color: `${theme.components.button.text.disabledColor} !important`,
      opacity: 0.5,
      '& .button-icon, & .button-text': {
        color: `${theme.components.button.text.disabledColor} !important`,
      },
    },
  },
}))

export default compose(withInfoTooltip)(Button)
